package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * M6-d：解析 screen 内的内联 component 声明：
 *
 * ```
 * component "itemCard" {
 *   props { item: Item }
 *   root  { ... }
 * }
 * ```
 *
 * 与跨文件 [ComponentParser] 的差异：
 * - 名字必须由字符串字面量给出（跨文件由资源路径推导，不允许字符串 id）
 * - 缺少 props 或 root 块直接报 [DslError.E711_INLINE_COMPONENT_MISSING_PROPS_OR_ROOT]
 * - 解析期把 [Parser.inComponentFile] 暂时置 true，使 root 内可写 `children` 占位
 */
internal class InlineComponentParser(private val p: Parser) {

    fun parse(): ComponentAst {
        val componentTok = p.expect(TokenType.KW_COMPONENT, "缺少 component 关键字")
        val nameTok = p.expect(TokenType.STRING, "内联 component 后必须跟字符串名字")
        val name = nameTok.literalValue as String
        p.expect(TokenType.LBRACE, "内联 component 头部后缺少 '{'")
        p.skipNewlines()

        val previousFlag = p.inComponentFile
        p.inComponentFile = true
        try {
            var props: List<ComponentAst.PropDecl>? = null
            var root: ElementAst? = null
            while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
                when (p.current().type) {
                    TokenType.KW_PROPS -> {
                        if (props != null) p.fail("内联 component '${name}' 的 props 块只能声明一次")
                        props = PropsBlockParser(p).parse()
                    }
                    TokenType.KW_ROOT -> {
                        if (root != null) p.fail("内联 component '${name}' 的 root 块只能声明一次")
                        root = ElementBlockParser(p).parseRoot()
                    }
                    else -> p.fail("内联 component 块内不识别的 token: '${p.current().lexeme}'")
                }
                p.skipNewlines()
            }
            val closeToken = p.expect(TokenType.RBRACE, "内联 component '${name}' 缺少 '}'")
            if (props == null || root == null) {
                p.fail(DslError.E711_INLINE_COMPONENT_MISSING_PROPS_OR_ROOT, componentTok.position, name)
            }
            return ComponentAst(
                id = name,
                props = props,
                root = root,
                animations = emptyList(),
                position = p.completePosition(componentTok.position, closeToken),
            )
        } finally {
            p.inComponentFile = previousFlag
        }
    }
}
